<template>
  <div data-component="App" class="position-relative full-height-container">
    <!-- ------------- loading & not-active tab overlays --------------- -->
    <b-overlay
        :show="showOverlay"
        id="global-overlay"
        variant="dark"
        :no-center="notActiveTab"
        no-wrap>
      <template v-if="notActiveTab" v-slot:overlay>
        <not-active-tab-overlay />
      </template>
    </b-overlay>

    <toast-notifications />
  
    <!-- ----------- logged in layout ---------------------------------- -->
    <template v-if="loggedInLayout">
      <gdpr-screen v-if="loggedIn" />
      <desktop-app v-if="desktopLayout" />
      <mobile-app v-else-if="mobileLayout" />
    </template>
    <!-- ----------- logged out layout --------------------------------- -->
    <!-- if we're not logged-in, use the logged-out layout unless we're
    still processing the bookmark -->
    <layout-logged-out v-else-if="loggedOutLayout">
      <b-container fluid class="p-0 full-height-container">
        <router-view />
      </b-container>
    </layout-logged-out>
    <!-- ----------- no content while spinning ------------------------- -->
    <template v-else-if="showOverlay">
      <!-- if we're logged out and the spinner is present -->
      <!-- don't display anything if we're evaluating bookmark -->
      <div class="fullscreen"/>
    </template>
    <div v-else>
      <div class="fullscreen">
        <b-spinner class="center-in-screen" :label="$t('label_loading')"></b-spinner>
      </div>
    </div>
    <colors-pip :pipOnly="true" />
  </div>
</template>

<script>
//---- components ---------
//---- mixins -------------
//---- services -----------
//---- other --------------
//---- 3rd-parties --------

//---- components ---------
import Colors from "./components/callcenter/tools/Colors.vue";
import DesktopApp from "./DesktopApp.vue";
import GdprScreen from "./components/shared/GDPRScreen.vue";
import LayoutLoggedOut from "./LayoutLoggedOut.vue";
import MobileApp from "./MobileApp.vue";
import NotActiveTabOverlay from "./components/shared/errors/NotActiveTabOverlay.vue";
import ToastNotifications from "./components/shared/errors/ToastNotifications.vue";
//---- mixins -------------
import ColorsMixin from "./components/callcenter/tools/colors.js";

//---- services -----------
import appLogger from "./services/app-logger";
// import AdminService from "@/app/services/admin.service.js";
import AuthService from "@/app/services/auth.service.js";
import checkWebPSupport from "@/app/services/browser.service.js";
import browserTabActivityService from "@/app/services/browser-tab-activity.service.js";
import KMOfflineConnectivityService from "@/app/services/kmoffline-connectivity.service.js"
//import NotificationService from "@/app/services/notification";
//---- 3rd-parties --------
import debounce from "lodash/debounce";
import { mapGetters } from "vuex";

export default {
  name: "app",

  mixins: [ColorsMixin],

  components: {

    DesktopApp,
    MobileApp,
    // AppFooter,
    // "error-boundary": ErrorBoundary,
    NotActiveTabOverlay,
    ToastNotifications,
    LayoutLoggedOut,
    "colors-pip": Colors,
    GdprScreen,
  },

  data() {
    return {
      intervalHandle: null, // handle on the timer that checks for active window and timeout
      debouncedTrackWindowSize: debounce(this.trackWindowSize, 100, {
        maxWait: 500,
      }),
    };
  }, // date()

  mounted() {
    this.$store.dispatch("header/initIsActiveTab");
    browserTabActivityService.setup();
    KMOfflineConnectivityService.setup();
  }, // end mounted()

  async created() {
    // extract the context information (/advisor/) from the url
    // this will be used to make ajax requests to the backend.
    // normally it should always be "advisor", but we never know,
    // some customers like beckman had customized this to something
    // else than advisor, so it's better not to hard-code the value.
    const serverName = self.location.origin;
    const context = /\/([^/]*).*/.exec(self.location.pathname)[1];
    this.$store.dispatch("setBackendUrl", serverName + "/" + context);

    // register the thread that checks for timeout and if the current
    // tab is the active tab.
    if (this.intervalHandle == null)
      this.intervalHandle = self.setInterval(
        AuthService.checkActiveWindowAndTimeout,
        1500
      ); // check every 2 seconds for active window and timeout

    // load style from server and apply them
    try {
      this.saveDefaultStyles();
     // this.applyStyles(await AdminService.initStyles());
    } catch (e) {
      appLogger.logClientSideErrorToServer(e);
    }

    // check if the browser supports the webp image format
    const component = this;
    checkWebPSupport("lossy", function (feature, isSupported) {
      component.$store.dispatch("webpLossySupport", isSupported);
    });

    // register an event listener to track the window size when it
    // is resized
    this.debouncedTrackWindowSize();
    // console.log("removeEventListener.debouncedTrackWindowSize");
    window.removeEventListener("resize", this.debouncedTrackWindowSize);
    // console.log("addEventListener.debouncedTrackWindowSize");
    window.addEventListener("resize", this.debouncedTrackWindowSize);
    // if (window.document.documentMode) {
    //   alert("IE is not supported");
    // }

    this.trackWindowSize()
  }, // end created()

  destroyed() {
    // console.log("removeEventListener.debouncedTrackWindowSize");
    window.removeEventListener("resize", this.debouncedTrackWindowSize);
    browserTabActivityService.tearDown();
    KMOfflineConnectivityService.tearDown();
  }, // end destroyed()

  computed: {
    loggedIn() {
      return this.$store.getters["auth/loggedIn"];
    },

    isLoginStatusKnown() {
      return this.$store.getters["auth/isLoginStatusKnown"];
    },

    isPageNotFound() {
      return this.$route?.name === "page-not-found";
    },

    globalLoading() {
      return this.$store.getters["globalLoadingOverlay"];
    },

    notActiveTab() {
      return !this.$store.getters["header/isActiveTab"];
    },

    loggedInLayout() {
      return this.isLoginStatusKnown && this.loggedIn || this.isPageNotFound;
    },

    loggedOutLayout() {
      return this.isLoginStatusKnown && !this.loggedInLayout && !this.showOverlay;
    //   const loggedOutLayout = this.isLoginStatusKnown && !this.loggedInLayout && !this.showOverlay;
    //   console.log("loggedOutLayout: " + loggedOutLayout);
    //   console.log("  loggedInLayout: " + this.loggedInLayout);
    //   console.log("  globalLoading: " + this.globalLoading);
    //   console.log("  notActiveTab: " + this.notActiveTab);
    //   return loggedOutLayout;
    },

    showOverlay() {
      const showOverlay = this.globalLoading || this.notActiveTab;

      try {
        if (showOverlay) {
          document
              .getElementsByTagName("BODY")[0]
              .classList.add("prevent-scrolling");
          } else {
          document
              .getElementsByTagName("BODY")[0]
              .classList.remove("prevent-scrolling");
          }
      } catch (ex) {
        console.error(ex);
      }

      return showOverlay;
    },

    ...mapGetters({
      isKMOffline: "kmOffline/isKMOffline",
      hServiceConnected: "kmOffline/isConnected",
      isLoginStatusKnown: "auth/isLoginStatusKnown",
      loggedIn: "auth/loggedIn",
    }),

  }, // end computed

  methods: {
    // clickAnywhere() {
    //     console.log('clickkk!')
    // },
    trackWindowSize() {
      const viewportSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      this.$store.dispatch("onViewportSizeChange", viewportSize);
    }, // end trackWindowSize()
  }, // end methods

  watch: {
    isKMOffline(kmOffline) {
        if (kmOffline) 
            KMOfflineConnectivityService.setup();
    }
  }, // end watch
};
</script>

<style>

body, html, .full-height-container {
    height: 100%;
}

/* -------------------------- CSS Variables -------------------------------- */

:root {
  /* Fonts */

  --body-font-family: apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji",
    "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";

  --monospace-font-family: "Courier New", Courier, monospace;

  --default-text-color: #212529;

  /* Color Palette */

  --primary-button-bg-color: #e46c0a;
  --primary-button-border-color: #e46c0a;
  --primary-button-color: white;
  --primary-button-bg-hover-color: #b65607;
  --primary-button-hover-color: white;

  --secondary-button-bg-color: #6e717c;
  --secondary-button-border-color: #6e717c;
  --secondary-button-color: white;
  --secondary-button-bg-hover-color: #5a6268;
  --secondary-button-hover-color: white;

  --transparent-button-color: #6e717c;
  --transparent-button-hover-color: #6e717c;

  --button-bar-bg-color: #e46c0a;
  --button-bar-color: white;

  --table-ptc-primary-bg-color: #e46c0a;
  --table-ptc-primary-color: white;

  --card-header-button-bg-color: #3d4451;
  --card-header-button-color: white;

  --quest-menu-bg-color: #e46c0a;
  --quest-menu-color: white;

  --result-title-color: #232b2d;
  --result-body-color: #6e717c;
  --result-footer-color: #6e717c;
  --result-value-color: #333;

  --project-menu-bg-color: #f7f7f7;

  --solution-compare-menu-bg-color: #e46c0a;

  --breadcrumb-bg-color: #e46c0a;

  --draft-comparison-menu-bg-color: #e46c0a;

  --chatbot-message-in-bg-color: #43c4f1;
  --chatbot-message-in-border-color: #c2c7ce;
  --chatbot-message-in-color: #212529;

  --chatbot-message-out-bg-color: #f7f7f7;
  --chatbot-message-out-border-color: #c2c7ce;
  --chatbot-message-out-color: black;

  --free-text-search-button-bg-color: #28a745;
  --free-text-search-button-bg-color-disabled: #6e717c;

  --expert-search-tray-button-border-color: #4caf50;
  --expert-search-tray-button-bg-color: white;
  --expert-search-tray-button-color: black;
  --expert-search-tray-color: #6e717c;
  --expert-search-tray-bg-color: #fefbae;
  --expert-search-tray-border-color: #ecd26a;

  --expert-class-bg-color: #d8eeff;

  --bold-primary-color: #43c4f1;
  --bold-secondary-color: #6e717c;

  --error-color: #dc3545;
}

/* -------------------------- Root Styles ---------------------------------- */

body {
  font-family: apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji",
    "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"; /* fallback for IE */
  font-family: var(--body-font-family);

  color: #212529; /* fallback for IE */
  color: var(--default-text-color);
  overflow-y: hidden;
}

/* -------------------------- Global Styles -------------------------------- */

.bg-kmoffline {
  background-color: #000d95 !important;
}
.bg-kmoffline-secondary {
  background-color: #000d95 !important;
}

.fullscreen {
  height: 100%;
  width: 100vw;
}
.center-in-screen {
  position: fixed;
  z-index: 999;
  overflow: visible;
  margin: auto;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 50px;
  height: 50px;
}

.inline-block {
  display: inline-block;
}

.float-right {
  float: right;
}

.a11y-hide {
  position: absolute;
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  overflow: hidden;
}

.clickable {
  cursor: pointer;
}

.non-clickable {
  cursor: default;
}

.avoid-page-break {
  page-break-inside: avoid;
}

.longtext {
  max-width: 170px;
  overflow: hidden !important;
  white-space: nowrap;
  text-overflow: ellipsis !important;
}

.shorttext {
  overflow: hidden;
  margin-right: 0.2rem;
}

.error {
  color: #dc3545; /* fallback for IE */
  color: var(--error-color);
}

@media only screen and (max-width: 760px) {
  .longtext {
    max-width: 150px;
  }
}

/* Use to make an element invisible (and not affect the rest of the layout) by rendering it off screen */
.off-screen {
  position: fixed;
  width: 0px;
  height: 0px;
  top: -5px;
  left: -5px;
  display: none;
}

/* The following flex-* classes are used for the full screen layout of mobile UI */

.flex-scrollable {
  display: flex;
  flex: 1 1 auto;
  overflow-y: auto; 
}

.flex-hidden {
  display: flex;
  flex: 1 1 auto;
  overflow-y: hidden;
}

.flex-container-vertical {
  display: flex;
  flex-direction: column;
}

.flex-distribute-v {
    justify-content: space-evenly;
}

.flex-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Use the remaining vertical height */
.flex-use-remaining-height {
  flex: 1 1 auto;
  overflow-y: hidden;
}

/* Use the remaining vertical height and make it's content scrollable
   (only use this in the inner-most container) */
.flex-remaining-height-scrollable {
  flex: 1 1 auto;
  overflow-y: auto;
}

/* --------------------------           ----------------------------------- */

img.placeholder {
  margin: 20px 10px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100%;
  background-color: rgba(0, 123, 255, 0.13) !important;
}

/* -------------------------- PTC Styles ----------------------------------- */

.btn-ptc-primary {
  border-radius: 4px;
  background-color: #e46c0a; /* fallback for IE-11 */
  background-color: var(--primary-button-bg-color);
  /* font-family: TODO open-sans; */
  font-weight: 600;
  /* font-size: 14pt; */
  color: white; /* fallback for IE */
  color: var(--primary-button-color);
}
.btn-ptc-primary:hover {
  border-radius: 4px;
  background-color: #b65607; /* fallback for IE */
  background-color: var(--primary-button-bg-hover-color);
  font-weight: 600;
  color: white; /* fallback for IE */
  color: var(--primary-button-hover-color);
}

.btn-ptc-secondary {
  border-radius: 4px;
  background-color: #6e717c; /* fallback for IE */
  background-color: var(--secondary-button-bg-color);
  /* font-family: TODO open-sans; */
  font-weight: 600;
  /* font-size: 14pt; */
  color: white; /* fallback for IE */
  color: var(--secondary-button-color);
}
.btn-ptc-secondary:hover {
  border-radius: 4px;
  background-color: #5a6268; /* fallback for IE */
  background-color: var(--secondary-button-bg-hover-color);
  /* font-family: TODO open-sans; */
  font-weight: 600;
  /* font-size: 14pt; */
  color: white; /* fallback for IE */
  color: var(--secondary-button-hover-color);
}

.table-ptc-primary {
  /* background-color: #17a2b8; */
  background-color: #e46c0a; /* fallback for IE */
  background-color: var(--table-ptc-primary-bg-color);
  color: white; /* fallback for IE */
  color: var(--table-ptc-primary-color);
}

/* tabs */
.ptc-primary .card-header {
  background-color: transparent;
  border: none;
}

.ptc-primary .card-header > ul {
  border-bottom: 2px solid #d8dbde;
}

.ptc-primary .card-header > ul > li {
  border: none;
  margin: 0px;
  margin-bottom: -2px;
}

.ptc-primary .card-header > ul > li > a {
  color: #6e717c;
  font-weight: 600;
  border: none;
}

.ptc-primary .card-header > ul > li > a:hover {
  color: #232b2d;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 2px solid #b65607;
}

.ptc-primary .card-header > ul > li > a.active {
  color: #232b2d;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 2px solid #e46c0a;
  margin-bottom: 0;
}

.ptc-primary .card-header > ul > li > a.disabled {
  font-weight: 600;
  color: #adb5bd;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 2px solid #d8dbde;
}

/* ------------------------------------------- */

.bold-primary {
  color: #43c4f1 !important; /* fallback for IE */
  color: var(--bold-primary-color) !important;
  font-weight: bolder;
}
.bold-secondary {
  color: 6e717c !important; /* fallback for IE */
  color: var(--bold-secondary-color) !important;
  font-weight: bolder;
}

/* ------------------------------------------- */

.btn-ptc-transparent {
  border-radius: 4px;
  background-color: transparent;
  /* font-family: TODO open-sans; */
  font-weight: 600;
  /* font-size: 14pt; */
  color: #6e717c; /* fallback for IE */
  color: var(--transparent-button-color);
}
.btn-ptc-transparent:hover {
  border-radius: 4px;
  background-color: transparent;
  /* font-family: TODO open-sans; */
  font-weight: 600;
  /* font-size: 14pt; */
  color: #6e717c; /* fallback for IE */
  color: var(--transparent-button-hover-color);
}

</style>